/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import {
  Injectable,
  Optional,
  Signal,
  SkipSelf,
  WritableSignal,
  computed,
  inject,
  signal
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

registerLocaleData(localeFr);
registerLocaleData(localeFr, 'fr_FR');
registerLocaleData(localeEn);
registerLocaleData(localeEn, 'en_US');

/**
 * A service used to store and manage the current locale for the app.
 */
@Injectable({
  providedIn: 'root'
})
export class OSKLocaleService {
  private readonly translate: TranslateService = inject(TranslateService);

  private readonly _locale: WritableSignal<string> = signal<string>(environment.defaultLocale);

  public readonly locale: Signal<string> = computed(() => this._locale());

  private initialized: boolean = false;
  /**
   * List of all registered locales
   */
  private readonly registeredLocales: string[] = ['fr', 'fr_FR', 'en', 'en_US'];

  constructor(@Optional() @SkipSelf() otherInstance: OSKLocaleService) {
    if (otherInstance) {
      throw 'OSKLocaleService must have only one instance';
    }
    this.registerLocale(environment.defaultLocale);
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.registerLocale(environment.defaultLocale);
    // TODO: Get user's saved locale when it is saved
    this._locale.set(environment.defaultLocale);
    this.initialized = true;
  }

  /**
   * Sets the given locale to be used in the app
   *
   * @param localeId The locale we want to use
   */
  async setLocale(localeId: string): Promise<void> {
    if (!localeId) {
      return;
    }

    if (!this.registeredLocales.includes(localeId)) {
      this.registerLocale(localeId);
    }

    await firstValueFrom(this.translate.use(localeId));
    this._locale.set(localeId);
  }

  /**
   *  Register the locale that we want to use
   *
   * @param { string } locale The locale id that we want to register
   */
  private registerLocale(locale: string): void {
    if (!locale) {
      return;
    }

    if (locale.startsWith('fr')) {
      registerLocaleData(localeFr);
      registerLocaleData(localeFr, locale);
    } else if (locale.startsWith('en')) {
      registerLocaleData(localeEn);
      registerLocaleData(localeEn, locale);
    }
    if (!this.registeredLocales.includes(locale)) {
      this.registeredLocales.push(locale);
    }
  }
}
